import React, { useEffect, useState } from "react";
import { DownloadSimple, X, ShareNetwork, PlusSquare, AppleLogo, Browser, CheckCircle, DotsThreeVertical } from "@phosphor-icons/react";

/**
 * Always-visible Install-PWA button.
 *
 * Renders whenever the app is NOT running in standalone (i.e. not yet
 * installed). Clicking the button:
 *
 *  - If the browser fired `beforeinstallprompt` → triggers the native
 *    OS-level install dialog directly.
 *  - On iOS Safari, or any other browser where the event doesn't fire,
 *    opens a small modal with step-by-step instructions tailored to the
 *    detected browser (iOS Safari, Chrome desktop, Edge, Firefox, etc.).
 *
 * Two visual variants:
 *  - `variant="primary"`: solid pill, navy bg — for landing/marketing CTAs.
 *  - `variant="ghost"`: outlined small chip — for in-app top bars.
 */
export default function InstallAppButton({ variant = "primary", className = "" }) {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  const isStandalone =
    typeof window !== "undefined" &&
    (window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator?.standalone === true);

  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const isIOS = /iPhone|iPad|iPod/.test(ua);
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
  const isFirefox = /Firefox/i.test(ua);
  const isEdge = /Edg/i.test(ua);
  const isChrome = /Chrome/i.test(ua) && !isEdge;
  const isAndroid = /Android/i.test(ua);

  useEffect(() => {
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
      setShowHelp(false);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed || isStandalone) return null;

  const onClick = async () => {
    if (deferred) {
      try {
        await deferred.prompt();
        const choice = await deferred.userChoice;
        if (choice?.outcome === "accepted") setInstalled(true);
      } catch { /* user dismissed */ }
      setDeferred(null);
    } else {
      // Browser hasn't fired the event yet (or doesn't support PWA install
      // automatically). Show instructions so the user can install manually.
      setShowHelp(true);
    }
  };

  const baseCls =
    variant === "primary"
      ? "h-11 px-5 bg-forest text-bg text-sm font-semibold hover:bg-forest-hover"
      : "h-9 px-3 border border-hairline text-xs font-mono uppercase tracking-widest hover:bg-bg";

  return (
    <>
      <button
        type="button"
        onClick={onClick}
        data-testid="install-app-btn"
        className={`${baseCls} inline-flex items-center gap-2 ${className}`}
      >
        <DownloadSimple size={variant === "primary" ? 16 : 13} weight="bold" />
        Instalar app
      </button>

      {showHelp && (
        <InstallHelpModal
          onClose={() => setShowHelp(false)}
          isIOS={isIOS}
          isSafari={isSafari}
          isFirefox={isFirefox}
          isAndroid={isAndroid}
          isChrome={isChrome}
          isEdge={isEdge}
        />
      )}
    </>
  );
}

function InstallHelpModal({ onClose, isIOS, isSafari, isFirefox, isAndroid, isChrome, isEdge }) {
  // Pick the best instruction set for the current browser.
  const browser =
    isIOS && isSafari ? "iosSafari"
    : isIOS ? "iosOther"
    : isAndroid && isChrome ? "androidChrome"
    : isAndroid && isFirefox ? "androidFirefox"
    : isFirefox ? "desktopFirefox"
    : isEdge ? "desktopEdge"
    : isChrome ? "desktopChrome"
    : "generic";

  const content = INSTRUCTIONS[browser] || INSTRUCTIONS.generic;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-up"
      onClick={onClose}
      data-testid="install-help-modal"
    >
      <div
        className="bg-surface w-full sm:max-w-md p-6 border border-hairline shadow-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 bg-forest text-bg flex items-center justify-center flex-shrink-0">
              {content.icon}
            </div>
            <div>
              <div className="font-display text-xl font-black leading-tight">{content.title}</div>
              <div className="label-mono mt-0.5">{content.subtitle}</div>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-bg" data-testid="install-help-close" aria-label="Cerrar">
            <X size={16} />
          </button>
        </div>

        <ol className="space-y-3 text-sm mb-6">
          {content.steps.map((s, i) => (
            <li key={i} className="flex items-start gap-3">
              <span className="w-7 h-7 bg-forest text-bg font-mono text-xs flex items-center justify-center flex-shrink-0">{i + 1}</span>
              <div className="flex-1 pt-0.5">{s}</div>
            </li>
          ))}
        </ol>

        <ul className="space-y-1.5 text-xs text-inkMuted mb-6 border-t border-hairline pt-4">
          <li className="flex items-center gap-2"><CheckCircle size={12} weight="fill" className="text-forest" /> Acceso sin abrir el navegador</li>
          <li className="flex items-center gap-2"><CheckCircle size={12} weight="fill" className="text-forest" /> Alertas push de chat, créditos y transferencias</li>
          <li className="flex items-center gap-2"><CheckCircle size={12} weight="fill" className="text-forest" /> Ícono propio en pantalla de inicio</li>
        </ul>

        <button
          onClick={onClose}
          className="w-full h-11 bg-forest text-bg text-sm font-semibold"
          data-testid="install-help-ok"
        >
          Entendido
        </button>
      </div>
    </div>
  );
}

const INSTRUCTIONS = {
  iosSafari: {
    icon: <AppleLogo size={22} weight="fill" />,
    title: "Instalar en iPhone",
    subtitle: "iOS · Safari",
    steps: [
      <>Toca el botón <b>Compartir</b> <ShareNetwork size={14} weight="bold" className="inline mb-0.5" /> en la barra inferior.</>,
      <>Desliza y elige <b>Agregar a pantalla de inicio</b> <PlusSquare size={14} weight="bold" className="inline mb-0.5" />.</>,
      <>Toca <b>Agregar</b> en la esquina superior derecha.</>,
    ],
  },
  iosOther: {
    icon: <AppleLogo size={22} weight="fill" />,
    title: "Instalar en iPhone",
    subtitle: "Abre desde Safari para instalar",
    steps: [
      <>Copia este sitio y ábrelo en <b>Safari</b> (no en otro navegador). En iPhone solo Safari permite instalar apps web.</>,
      <>En Safari, toca <b>Compartir</b> <ShareNetwork size={14} weight="bold" className="inline mb-0.5" /> → <b>Agregar a pantalla de inicio</b>.</>,
      <>Confirma con <b>Agregar</b>.</>,
    ],
  },
  androidChrome: {
    icon: <DownloadSimple size={22} weight="bold" />,
    title: "Instalar en Android",
    subtitle: "Chrome",
    steps: [
      <>Toca el menú <b>⋮</b> <DotsThreeVertical size={14} weight="bold" className="inline mb-0.5" /> en la esquina superior derecha.</>,
      <>Elige <b>Instalar app</b> (o <b>Agregar a pantalla de inicio</b>).</>,
      <>Confirma con <b>Instalar</b>.</>,
    ],
  },
  androidFirefox: {
    icon: <Browser size={22} weight="duotone" />,
    title: "Instalar en Android",
    subtitle: "Firefox",
    steps: [
      <>Toca el menú <b>⋮</b> en la esquina superior derecha.</>,
      <>Elige <b>Instalar</b> o <b>Agregar a pantalla de inicio</b>.</>,
      <>Confirma con <b>Agregar</b>.</>,
    ],
  },
  desktopChrome: {
    icon: <DownloadSimple size={22} weight="bold" />,
    title: "Instalar en escritorio",
    subtitle: "Chrome / Brave / Opera",
    steps: [
      <>Mira el ícono de <b>instalación</b> <DownloadSimple size={14} weight="bold" className="inline mb-0.5" /> al final de la barra de direcciones.</>,
      <>Haz clic y confirma <b>Instalar</b>.</>,
      <>Si no aparece, abre el menú <b>⋮</b> → <b>Instalar UNIK VENTURES</b>.</>,
    ],
  },
  desktopEdge: {
    icon: <DownloadSimple size={22} weight="bold" />,
    title: "Instalar en escritorio",
    subtitle: "Microsoft Edge",
    steps: [
      <>Haz clic en el menú <b>···</b> arriba a la derecha.</>,
      <>Elige <b>Apps</b> → <b>Instalar este sitio como aplicación</b>.</>,
      <>Confirma con <b>Instalar</b>.</>,
    ],
  },
  desktopFirefox: {
    icon: <Browser size={22} weight="duotone" />,
    title: "Firefox no soporta instalar apps web",
    subtitle: "Usa Chrome, Edge o Brave",
    steps: [
      <>Firefox en escritorio actualmente no permite instalar PWAs.</>,
      <>Abre este sitio en <b>Chrome</b>, <b>Edge</b> o <b>Brave</b> y vuelve a tocar <b>Instalar app</b>.</>,
      <>Alternativa: úsalo desde el navegador, los pushes funcionan igual.</>,
    ],
  },
  generic: {
    icon: <DownloadSimple size={22} weight="bold" />,
    title: "Instalar UNIK VENTURES",
    subtitle: "Tu navegador",
    steps: [
      <>Busca en el menú de tu navegador la opción <b>Instalar app</b> o <b>Agregar a pantalla de inicio</b>.</>,
      <>Confirma cuando te pregunte.</>,
      <>Si no aparece la opción, prueba abrir el sitio en <b>Chrome</b> o <b>Edge</b>.</>,
    ],
  },
};
